import React, { useEffect, useState, useCallback } from "react";
import TextStyles from "../../Elements/TextStyles/TextStyles";
import { ReactComponent as BackIcon } from "../../../assets/svgs/back-button.svg";
import CardsBorder from "../../Cards/CardsBorder";
import UserDetails from "./UserDetails";
import AssignedForm from "./AssignedForm";
import { getApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { useDebounce } from "../../../hooks/debouncing";
const UserDetailPage = () => {
  const [userDetails, setUserDetails] = useState({});
  const [formAllotmentData, setFormAllotmentData] = useState([]);

  // Pagination + Search
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [pageNo, setPageNo] = useState(1);
  const [skip, setSkip] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();
  const debouncedSearch = useDebounce(search, 500);
  const { userId, emp_id, projectPage, customerPage, clientId, projectId } = location.state || {};

  // API CALL
  const fetchUserDetails = async () => {
    try {
      const response = await getApi({
        apiUrl: projectId
          ? API_URLS.GET_CUSTOMER_FORMS_BY_PROJECT_ID(userId, projectId, pageSize, skip, debouncedSearch)
          : API_URLS.GET_USER_BY_USERID(userId, debouncedSearch, pageSize, skip),
        headers: { client: clientId },
      });

      if (!response?.success) return;

      // ── GET_CUSTOMER_FORMS_BY_PROJECT_ID response ──────────────────────────
      // ── GET_CUSTOMER_FORMS_BY_PROJECT_ID response ──────────────────────────
if (projectId) {
  const emp = response.data?.employee || {};
  setUserDetails({
    userId: emp_id || "-",
    email: emp.email || "-",
    createdDate: emp.userCreatedDate ? dayjs(emp.userCreatedDate).format("DD MMM YYYY") : "-",
    role: Array.isArray(emp.role) ? emp.role.join(", ") : emp.role || "-",
    group: emp.group || "-",
    organization: emp.organization || "-",
  });

  // Normalise flat items into the shape AssignedForm expects
  const forms = response.data?.forms || [];
  const allotmentList = forms.map((item) => ({
    // Mimic nested shape so AssignedForm can read existing field paths
    _id: item.standardFormAllotmentId || item._id,
    template: { _id: item.formId || item._id, title: item.formTitle },
    project: { name: item.projectTitle },
    createdAt: item.assignedOn,
    user: [{ status: item.status }],
  }));

  setFormAllotmentData(allotmentList);
  setTotalRecords(response.total ?? allotmentList.length);
  return;
}

      // ── GET_USER_BY_USERID response ────────────────────────────────────────
      // { success, data: [{ employee_id, name, email, ... , formAllotments }] }
      if (Array.isArray(response.data) && response.data.length > 0) {
        const data = response.data[0];
        const backendLimit = response.limit ?? pageSize ?? 10;
        const backendSkip = response.skip ?? skip ?? 0;
        const allotmentList = data.formAllotments || data.formAllotment || data.allotments || [];
        const backendTotal = Math.max(
          response.total ?? 0,
          response.totalAllotments ?? 0,
          response.totalCount ?? 0,
          allotmentList.length
        );

        setUserDetails({
          userId: emp_id,
          name: data.name,
          email: data.email,
          createdDate: dayjs(data.createdAt).format("DD MMM YYYY"),
          role: Array.isArray(data.role) ? data.role.join(", ") : data.role || "-",
          group: "-",
          organization: "-",
        });

        setFormAllotmentData(allotmentList);
        setPageSize(backendLimit);
        setSkip(backendSkip);
        setPageNo(Math.floor(backendSkip / backendLimit) + 1);
        setTotalRecords(backendTotal);
      }
    } catch (error) {
      console.log("Unable to get user details", error);
    }
  };

  // Pagination Handler
  const handlePageChange = useCallback((page, size) => {
    setPageNo(page);
    setPageSize(size);
    setSkip((page - 1) * size);
  }, []);

  useEffect(() => {
    if (userId && clientId) {
      fetchUserDetails();
    }
  }, [userId, clientId, debouncedSearch, skip, pageSize]);

  return (
    <div className="p-10">
      {/* Breadcrumb */}
      <div className="flex justify-end text-xs">
        <p>Home / Projects / </p>
        <TextStyles color="gradient">User Detail Pages</TextStyles>
      </div>

      {/* Back + Title */}
      <div className="flex items-center gap-3 mt-3 cursor-pointer">
        <BackIcon
          onClick={() => {
            if (projectPage) {
              navigate("/projects/project-page", { state: { projectId } });
            } else {
              navigate(`/customer/customer-page/${clientId}`);
            }
          }}
        />
        <TextStyles color="gradient" style={{ fontSize: "20px" }}>
          User Detail Page
        </TextStyles>
      </div>

      {/* User Details */}
      <div className="mt-5">
        <CardsBorder>
          <UserDetails userDetails={userDetails} />
        </CardsBorder>
      </div>

      {/* Assigned Forms */}
      <div className="mt-5">
        <CardsBorder>
          <AssignedForm
            formAllotmentData={formAllotmentData}
            setSearch={setSearch}
            handlePageChange={handlePageChange}
            pageSize={pageSize}
            pageNo={pageNo}
            total={totalRecords}
            setPageNo={setPageNo}
            setSkip={setSkip}
          />
        </CardsBorder>
      </div>
    </div>
  );
};

export default UserDetailPage;
